import { type LeadAction } from "@/api/app-configuration.api";
import { leadsApi } from "@/api/leads.api";
import { useAvailableIntegrationsQuery } from "@/queries/integrations";
import { isAdmin } from "@/services/auth.service";
import {
  Campaign,
  LeadDeliveryStatusEnum,
  type Lead,
  type LeadFeedback,
} from "@/types/campaign";
import { Entries } from "@/types/utility";
import { queryClient } from "@/utils/api";
import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import { useRouteContext } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { CircleAlert, Loader2 } from "lucide-react";
import { DynamicIcon } from "lucide-react/dynamic";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { DialogTrigger, Button as RACButton } from "react-aria-components";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import { Button } from "../generic/Button";
import { Calendar } from "../generic/Calendar";
import { Dialog } from "../generic/Dialog";
import { Heading } from "../generic/Heading";
import { Modal } from "../generic/Modal";
import { Popover } from "../generic/Popover";
import { Separator } from "../generic/Separator";

export type ActionBarProps = {
  campaign: Campaign;
  currentLeadStatus: Lead["status"];
  lead: Lead;
  onAction: (
    status: Lead["status"],
    action: string,
    followUpTime?: string,
    feedback?: LeadFeedback,
  ) => void;
  disableActions?: boolean;
  thirdPartyLink?: string | null;
};

export function ActionBar({
  campaign,
  currentLeadStatus,
  lead,
  onAction,
  disableActions = false,
  thirdPartyLink,
}: ActionBarProps) {
  const context = useRouteContext({ from: "/player" });
  const [leadStatus, setLeadStatus] =
    useState<Lead["status"]>(currentLeadStatus);

  const order = useMemo(
    () => ({
      "thumbs-down": 1,
      "alarm-clock": 2,
      "thumbs-up": 3,
    }),
    [],
  );

  const actionCallback = async (
    status: Lead["status"],
    action: string,
    followUpTime?: string,
    feedback?: LeadFeedback,
  ) => {
    if (leadActivityAction) setLeadActivityAction(null);
    resetStatusReason();
    onAction(status, action, followUpTime, feedback);
  };

  const statusColors = useMemo(
    () => ({
      "thumbs-down":
        "bg-red-500/0 transition-colors duration-200 active:bg-red-500 hover:bg-red-500",
      "alarm-clock":
        "transition-colors duration-200 active:bg-theme-on-background-hover hover:bg-theme-on-background-hover",
      "thumbs-up":
        "bg-green-500/0 transition-colors duration-200 active:bg-green-500 hover:bg-green-500",
    }),
    [],
  );
  const getStatusBgColor = useCallback(
    (categoryIcon: LeadAction["categoryIcon"]) => {
      switch (categoryIcon) {
        case "thumbs-down":
          return "bg-red-500";
        case "alarm-clock":
          return "bg-theme-on-background-hover  border-theme-on-background-variant/50";
        case "thumbs-up":
          return "bg-green-500";
      }
    },
    [],
  );

  const groupedStatuses = useMemo(() => {
    return context.appConfiguration?.leadActions
      .sort((a, b) => {
        const orderA = order[a.categoryIcon] ?? 0;
        const orderB = order[b.categoryIcon] ?? 0;
        return orderA - orderB;
      })
      .reduce(
        (acc, action) => {
          if (!acc[action.categoryIcon]) {
            acc[action.categoryIcon] = {
              actions: [],
              color: statusColors[action.categoryIcon],
            };
          }
          acc[action.categoryIcon].actions.push(action);
          return acc;
        },
        {} as Record<
          LeadAction["categoryIcon"],
          { actions: LeadAction[]; color: string }
        >,
      );
  }, [context.appConfiguration?.leadActions, statusColors, order]);

  const [statusReason, setStatusReason] = useState<string>("");

  const [isDialogOpen, setIsDialogOpen] = useState<Record<number, boolean>>({});

  const [selectedAction, setSelectedAction] = useState<LeadAction | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
  const [showTimePicker, setShowTimePicker] = useState<boolean>(false);
  const [followUpDate, setFollowUpDate] = useState<CalendarDate | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackReason, setFeedbackReason] = useState<LeadFeedback | null>(
    null,
  );
  const [leadActivityAction, setLeadActivityAction] =
    useState<LeadAction | null>(null);
  const { data: integrations = [], isFetching: isFetchingIntegrations } =
    useAvailableIntegrationsQuery();
  const campaignIntegration = useMemo(() => {
    const integrationsMap = new Map(integrations.map((i) => [i.uniqueName, i]));
    return campaign.exportSystem
      ? integrationsMap.get(campaign.exportSystem)
      : null;
  }, [campaign.exportSystem, integrations]);

  const timePickerContainerRef = useRef<HTMLDivElement>(null);

  const resetStatusReason = () => {
    setLeadActivityAction(null);
    setSelectedAction(null);
    setIsDialogOpen((prev) => {
      const newState = { ...prev };
      Object.keys(newState).forEach((key) => {
        newState[Number(key)] = false;
      });
      return newState;
    });
    setStatusReason("");
    setIsCalendarOpen(false);
    setShowTimePicker(false);
    setFollowUpDate(null);
    setShowFeedback(false);
    setFeedbackReason(null);
  };

  const reasonAction = (action: LeadAction) => {
    setSelectedAction(action);
    setLeadStatus(action.status);
    setStatusReason(action.reason);
    if (action.feedback) {
      setShowFeedback(true);
    }
    // if action has a preset follow-up time, call onAction directly
    else if (action.presetFollowUp) {
      actionCallback(action.status, action.reason, action.presetFollowUp);
      // if reason has followup time, show calendar
    } else if (action.hasFollowUpTime) {
      setIsCalendarOpen(true);
      setShowTimePicker(false);
    } else {
      actionCallback(
        action.status,
        action.reason,
        undefined,
        feedbackReason ?? undefined,
      );
    }
  };

  const setDate = (date: CalendarDate) => {
    // Create a JavaScript Date object from the CalendarDate
    const jsDate = date.toDate(getLocalTimeZone());
    // Set the time to 8:00 AM
    jsDate.setHours(8, 0, 0, 0);

    // Close the calendar and time picker
    setIsCalendarOpen(false);
    setShowTimePicker(false);

    // Call onAction with the selected date at 8:00 AM
    actionCallback(leadStatus, statusReason, jsDate.toISOString());
  };

  const updateDate = (date: CalendarDate) => {
    setFollowUpDate(date);
    setShowTimePicker(true);
    if (timePickerContainerRef.current) {
      // Use a small timeout to ensure the DOM is fully rendered
      const scrollTimer = setTimeout(() => {
        const buttons = timePickerContainerRef.current?.querySelectorAll(
          "button:not(:disabled)",
        );

        if (buttons && buttons.length > 0) {
          // Use first enabled button
          (buttons[0] as HTMLElement).scrollIntoView({
            behavior: "smooth",
            block: "start", // Center the element in the viewport for better visibility
          });
        }
      }, 100); // Small delay to ensure DOM update

      return () => clearTimeout(scrollTimer);
    } else {
      requestAnimationFrame(() => {
        if (timePickerContainerRef.current) {
          const scrollTimer = setTimeout(() => {
            const buttons = timePickerContainerRef.current?.querySelectorAll(
              "button:not(:disabled)",
            );

            if (buttons && buttons.length > 0) {
              // Use first enabled button
              (buttons[0] as HTMLElement).scrollIntoView({
                behavior: "smooth",
                block: "start", // Center the element in the viewport for better visibility
              });
            }
          }, 100); // Small delay to ensure DOM update

          return () => clearTimeout(scrollTimer);
        }
      });
    }
  };

  const selectTime = (hour: number) => {
    if (!followUpDate) return;
    const date = followUpDate.toDate(getLocalTimeZone());
    date.setHours(hour, 0, 0, 0);
    setIsCalendarOpen(false);
    setShowTimePicker(false);
    // Here you would typically combine the date and time for the final action
    actionCallback(leadStatus, statusReason, date.toISOString());
  };

  const renderTimePicker = () => {
    const workingHours = Array.from({ length: 10 }, (_, i) => i + 8); // 8 AM to 5 PM
    const isToday = followUpDate?.compare(today(getLocalTimeZone())) === 0;
    const currentHour = new Date().getHours();

    return (
      <motion.div
        className="flex w-full flex-col justify-center"
        style={{ height: "328px" }} // Match approximate calendar height
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <h3 className="mt-2 mb-4 text-center text-sm font-medium">Tidspunkt</h3>
        <div
          ref={timePickerContainerRef}
          className="flex flex-col gap-4 overflow-y-auto"
          style={{
            height: "calc(100%)", // Total height minus header
          }}
        >
          {workingHours.map((hour) => {
            const isPastHour = isToday && hour <= currentHour;
            return (
              <Button
                key={hour}
                intent="secondary"
                onPress={() => selectTime(hour)}
                className="shrink-0"
                isDisabled={isPastHour}
                aria-label={
                  isPastHour ? "Tidspunkt er allerede passeret" : undefined
                }
                data-available={!isPastHour ? "true" : "false"}
              >
                {hour}:00
              </Button>
            );
          })}
        </div>
      </motion.div>
    );
  };

  function renderReasonButtons(actions: LeadAction[] | undefined) {
    if (!actions) return null;
    const hasFollowUpTime = actions.some((a) => a.hasFollowUpTime);

    if (showFeedback) {
      return context.appConfiguration?.feedbackTypes
        .filter((f) => selectedAction?.feedback?.includes(f.feedback))
        .map((feedback, index) => {
          return (
            <>
              {index === 0 && (
                <div className="text-theme-on-background-variant mb-12 w-full text-xs font-medium">
                  Kunne ikke få kontakt fordi
                </div>
              )}
              <RACButton
                className="hover:bg-theme-on-background-hover flex w-full cursor-pointer flex-col rounded-lg px-5 py-4 text-left transition-colors duration-200"
                key={feedback.feedback}
                onPress={() => setFeedbackReason(feedback.feedback)}
              >
                {feedback.description}
              </RACButton>
              {index !==
                (context.appConfiguration?.feedbackTypes.length ?? 0) - 1 && (
                <Separator className="my-4 w-full" />
              )}
            </>
          );
        });
    }

    if (hasFollowUpTime && isCalendarOpen) {
      return (
        <motion.div className="flex flex-col gap-y-10">
          {/* <div className="grow shrink-0 text-xs font-medium flex gap-x-2 items-center">
            <span>{statusReason}</span>
            <ChevronRight className="size-4 text-theme-on-background-variant" />
            <span className="text-theme-on-background-variant">Vælg dato</span>
          </div> */}
          <div className="flex shrink-0 grow items-stretch justify-center gap-x-6">
            <Calendar
              className="self-center"
              aria-label="Opfølgning dato"
              value={followUpDate}
              minValue={today(getLocalTimeZone())}
              onChange={updateDate}
            />
            <Separator className="my-4 mr-2 h-auto" orientation="vertical" />
            <div className="mr-4 flex flex-col gap-y-4 self-center">
              {showTimePicker ? (
                renderTimePicker()
              ) : (
                <>
                  <Button
                    intent="secondary"
                    onPress={() =>
                      setDate(today(getLocalTimeZone()).add({ days: 1 }))
                    }
                  >
                    I morgen
                  </Button>
                  <Button
                    intent="secondary"
                    onPress={() =>
                      setDate(today(getLocalTimeZone()).add({ weeks: 1 }))
                    }
                  >
                    Om en uge
                  </Button>
                  <Button
                    intent="secondary"
                    onPress={() =>
                      setDate(today(getLocalTimeZone()).add({ weeks: 2 }))
                    }
                  >
                    Om 14 dage
                  </Button>
                  <Button
                    intent="secondary"
                    onPress={() =>
                      setDate(today(getLocalTimeZone()).add({ months: 1 }))
                    }
                  >
                    Om en måned
                  </Button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      );
    }
    return actions.map((action, key) => (
      <React.Fragment key={key}>
        {key !== 0 && <Separator className="my-4 w-full" />}

        <motion.button
          key={key}
          onClick={() => reasonAction(action)}
          className="hover:bg-theme-on-background-hover flex w-80 cursor-pointer flex-col justify-center gap-y-1 rounded-lg px-5 py-6 transition-colors duration-200"
        >
          <div className="flex items-center gap-x-2">
            <div className="text-base leading-6 font-medium">
              {action.reason}
            </div>
          </div>
          <p className="text-theme-on-background-variant text-left text-xs leading-5">
            {action.description}
          </p>
        </motion.button>
      </React.Fragment>
    ));
  }

  async function retryLeadExport() {
    console.debug(`Retrying export for lead ID: ${lead.id}`);
    const response = await leadsApi.retryExport(lead.id);
    // The try/finally block ensures that we invalidate the query regardless of success or failure. Even though this looks wrong, it works as intended.
    try {
      if (response.success) {
        return response;
      }
      throw response;
    } finally {
      queryClient.invalidateQueries({
        queryKey: ["lead", lead.id],
      });
    }
  }

  if (
    !isFetchingIntegrations &&
    lead.deliveryStatus === LeadDeliveryStatusEnum.IntegrationFailed
  ) {
    return (
      <div className="flex gap-x-4">
        <DialogTrigger>
          <RACButton className="hover:text-theme-active flex cursor-pointer underline transition-colors duration-200">
            Kunne ikke levere til {campaignIntegration?.name}
            <CircleAlert className="ml-2 size-4" />
          </RACButton>
          <Modal>
            <Heading level={2} className="text-md font-medium">
              Integrationsfejl
            </Heading>
            <div className="mt-6 flex w-full flex-col gap-y-2">
              {lead.deliveryError ? (
                <>
                  <p className="text-theme-on-background-variant text-sm">
                    Levering til integration fejlede med årsagen:
                  </p>
                  <div className="text-theme-on-background-variant mt-4 w-full rounded-lg text-sm leading-normal text-wrap wrap-break-word italic">
                    {lead.deliveryError}
                  </div>
                </>
              ) : (
                <div className="text-theme-on-background-variant text-sm">
                  Der opstod en ukendt fejl under levering.
                </div>
              )}

              {isAdmin(context.user) && (
                <div className="mt-4 flex flex-col gap-y-2">
                  {campaign.exportMode === "SalesAutomatic" && (
                    <Button
                      intent="secondary"
                      onPressStart={() => {
                        console.debug(
                          `Retrying export for lead ID: ${lead.id}`,
                        );
                        toast.promise(retryLeadExport(), {
                          loading: "Leverer lead til eksternt system...",
                          success: "Lead leveret succesfuldt!",
                          error: () => {
                            return {
                              message: "Fejl ved levering af lead",
                              description: `Der opstod en fejl under leveringen af leadet til ${campaignIntegration?.name ?? "det eksterne system"}. Prøv igen senere.`,
                            };
                          },
                        });
                      }}
                    >
                      Prøv igen
                    </Button>
                  )}
                  <Button
                    intent="secondary"
                    link={{
                      to: "/settings/integrations/$integration/settings",
                      params: { integration: campaign.exportSystem || "" },
                    }}
                  >
                    Gå til integrationsindstillinger
                  </Button>
                </div>
              )}
            </div>
          </Modal>
        </DialogTrigger>
      </div>
    );
  }

  if (thirdPartyLink) {
    if (isFetchingIntegrations || !campaignIntegration) {
      return (
        <Loader2 className="text-theme-on-background size-10 animate-spin" />
      );
    }
    return (
      <div className="flex gap-x-4">
        <a
          href={thirdPartyLink}
          target="_blank"
          /* rel="noopener noreferrer" is essential for all links that use target="_blank" to open a new tab/window to an external site (like a lead in HubSpot).
            1. noopener: Prevents a security attack called tabnabbing by blocking the new tab from accessing the original window using window.opener. This protects our platform's security.
            2. noreferrer:
            a) Privacy: Prevents our page's URL from being sent to the external site via the HTTP Referer header.
            b) Security: Acts as a fallback for older browsers that may not fully support noopener.
          */
          rel="noopener noreferrer"
          className="hover:text-theme-active underline transition-colors"
        >
          Vis lead i {campaignIntegration?.name}
        </a>
      </div>
    );
  }

  return (
    <div className="flex gap-x-4">
      {groupedStatuses &&
        (
          Object.entries(groupedStatuses) as Entries<typeof groupedStatuses>
        ).map(([icon, action], index) => (
          <DialogTrigger
            key={icon}
            isOpen={isDialogOpen[index]}
            onOpenChange={(open) => {
              setIsDialogOpen((prev) => ({ ...prev, [index]: open }));
              if (!open) resetStatusReason();
            }}
          >
            <Button
              id={`action-bar-button-${icon}`}
              intent="secondary"
              size="rounded"
              key={icon}
              isDisabled={disableActions}
              className={twMerge(
                "min-w-auto p-3.5",
                action.color,
                isDialogOpen[index] && getStatusBgColor(icon),
              )}
            >
              <DynamicIcon name={icon} size={20} />
            </Button>
            <Popover
              className="rounded-xl"
              placement="top"
              offset={32}
              showArrow
            >
              <Dialog className="overflow-hidden rounded-xl bg-transparent">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={icon + "_dialog"}
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: 1,
                      transition: { duration: 0.2, ease: "easeOut" },
                    }}
                    exit={{
                      opacity: 0,
                      transition: { duration: 0.3, ease: "easeIn" },
                    }}
                  >
                    {renderReasonButtons(action.actions)}
                  </motion.div>
                </AnimatePresence>
              </Dialog>
            </Popover>
          </DialogTrigger>
        ))}
    </div>
  );
}
